import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

export default function PrivacyPolicy() {
  const { theme } = useTheme();
  const logoSrc = theme === "light" ? "/logo-light.png" : "/logo.png";

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg-base)",
      padding: "48px 24px",
      color: "var(--text-primary)",
    }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <Link to="/login" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)", textDecoration: "none", marginBottom: 28 }}>
            ← Volver al inicio
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <img src={logoSrc} alt="Creativedog" style={{ height: 28 }} />
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "var(--text-primary)", marginBottom: 8 }}>
            Políticas de Privacidad
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-muted)" }}>
            Última actualización: abril de 2026
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>

          <Section title="1. Sobre esta plataforma">
            <p>
              Smith es una plataforma interna de <strong>Creativedog Agency</strong> que centraliza el trabajo con agentes de inteligencia artificial para la gestión de clientes de marketing digital. El acceso está restringido a miembros del equipo de Creativedog y colaboradores externos expresamente autorizados.
            </p>
            <p>
              Esta política describe cómo Smith accede, usa, almacena y comparte los datos obtenidos a través de las APIs de Google, en cumplimiento con los{" "}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>
                Requisitos de Datos de Usuario de los Servicios de API de Google
              </a>.
            </p>
          </Section>

          <Section title="2. Datos que recopilamos">
            <p>Al autenticarte con Google, accedemos a los siguientes datos de tu cuenta:</p>
            <ul>
              <li><strong>Nombre completo</strong> — para identificarte dentro de la plataforma.</li>
              <li><strong>Dirección de email</strong> — usada como identificador único y para verificar el acceso autorizado.</li>
              <li><strong>Foto de perfil</strong> — para mostrar tu avatar en la interfaz.</li>
              <li><strong>Token de acceso a Google</strong> — utilizado para las integraciones con Google Drive y Gmail descritas en la sección 3.</li>
            </ul>
            <p>
              Adicionalmente, la plataforma registra el historial de conversaciones con los agentes de IA y el consumo de tokens de la API de Anthropic asociado a cada sesión.
            </p>
          </Section>

          <Section title="3. Uso de datos de Google API">
            <p>
              Smith utiliza las siguientes APIs de Google con los propósitos específicos detallados a continuación. El uso de los datos de Google API se limita estrictamente a las funcionalidades descriptas; no se transfieren, venden ni utilizan para publicidad.
            </p>

            <ScopeBlock
              scope="Perfil de Google (profile, email)"
              icon="👤"
              data="Nombre, dirección de email y foto de perfil pública."
              use="Autenticar al usuario, gestionar el acceso a la plataforma y mostrar el avatar en la interfaz. El email se utiliza para verificar que el usuario pertenece al dominio autorizado o está en la lista de acceso explícito."
              shared="No se comparte con terceros. Los datos se almacenan en la base de datos interna de Smith."
            />

            <ScopeBlock
              scope="Google Drive — Solo lectura (drive.readonly)"
              icon="📁"
              data="Metadatos y contenido de archivos en carpetas de Google Drive que el administrador de la plataforma configure para cada cliente."
              use="Sincronizar documentos de clientes (reportes, documentos de trabajo) desde carpetas de Google Drive hacia la base de datos interna de Smith, para que los agentes de IA puedan utilizar ese contexto al responder consultas. No se modifican, crean ni eliminan archivos en Drive."
              shared="El contenido extraído de los archivos se almacena en la base de datos interna de Smith. Fragmentos de ese contenido pueden ser enviados a la API de Anthropic (Claude) como contexto para generar respuestas. No se comparte con ningún otro tercero."
            />

            <ScopeBlock
              scope="Google Docs — Solo lectura (documents.readonly)"
              icon="📄"
              data="Contenido textual de documentos de Google Docs."
              use="Extraer el texto de documentos de Google Docs vinculados a carpetas de clientes para incorporarlo como contexto en las conversaciones con los agentes de IA."
              shared="Igual que drive.readonly: el contenido se almacena en Smith y puede ser enviado a Anthropic como contexto. No se comparte con ningún otro tercero."
            />

            <ScopeBlock
              scope="Gmail — Solo envío (gmail.send)"
              icon="✉️"
              data="Esta API se utiliza únicamente para enviar emails en nombre del usuario. Smith NO lee, accede ni almacena el contenido del buzón de Gmail del usuario, ni sus correos recibidos, ni ningún hilo de conversación existente."
              use="El agente PM puede redactar y enviar emails a clientes externos (por ejemplo, para solicitar información o enviar recordatorios), usando la cuenta de Gmail del usuario autenticado como remitente. El usuario siempre revisa y aprueba el contenido antes del envío desde la interfaz de Smith."
              shared="El contenido del email se procesa internamente en Smith. Se guarda un registro del email enviado (asunto, destinatario, cuerpo, fecha) en la base de datos interna. No se comparte con terceros salvo con el destinatario del email."
            />
          </Section>

          <Section title="4. Almacenamiento y seguridad">
            <p>
              Los datos se almacenan en una base de datos PostgreSQL alojada en un servidor privado bajo control exclusivo de Creativedog Agency. Las comunicaciones entre el cliente y el servidor se realizan mediante HTTPS con certificado SSL. Los tokens de autenticación se gestionan mediante JWT almacenados en el navegador.
            </p>
            <p>
              Los tokens de acceso a Google se almacenan en la base de datos exclusivamente para permitir las integraciones con Drive y Gmail descritas en la sección 3. No se almacenan tokens de refresh de Google a largo plazo; si el token expira, el usuario debe re-autenticarse.
            </p>
            <p>
              No compartimos ningún dato personal con terceros más allá de los proveedores directamente necesarios para el funcionamiento de la plataforma:
            </p>
            <ul>
              <li>
                <strong>Anthropic</strong> — proveedor del modelo de IA Claude. Fragmentos de conversaciones y documentos de clientes pueden ser procesados por su API.{" "}
                <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>Política de privacidad de Anthropic →</a>
              </li>
              <li>
                <strong>Google</strong> — proveedor de autenticación OAuth y servicios Drive/Gmail.{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>Política de privacidad de Google →</a>
              </li>
            </ul>
            <p>
              El uso que Smith hace de la información recibida de las APIs de Google cumple con la{" "}
              <a href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>
                Política de Datos de Usuario de los Servicios de API de Google
              </a>
              , incluidos los requisitos de uso limitado.
            </p>
          </Section>

          <Section title="5. Retención y eliminación de datos">
            <p>
              Los datos de usuario (nombre, email, foto de perfil) se conservan mientras la cuenta esté activa en la plataforma. El historial de conversaciones con los agentes se retiene como parte del contexto operativo de la agencia.
            </p>
            <p>
              Si necesitás eliminar tu cuenta y todos los datos asociados (perfil, historial de conversaciones, tokens de acceso), podés solicitarlo escribiendo a{" "}
              <a href="mailto:hello@creativedog.agency" style={{ color: "var(--accent)" }}>hello@creativedog.agency</a>. La eliminación se procesará dentro de los 30 días corridos.
            </p>
            <p>
              También podés revocar el acceso de Smith a tu cuenta de Google en cualquier momento desde la página de{" "}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>permisos de tu cuenta de Google</a>.
              Al revocar el acceso, las integraciones con Drive y Gmail dejarán de funcionar hasta que re-autentiques.
            </p>
          </Section>

          <Section title="6. Tus derechos">
            <p>Tenés derecho a:</p>
            <ul>
              <li>Conocer qué datos tuyos están almacenados en la plataforma.</li>
              <li>Solicitar la corrección de datos incorrectos.</li>
              <li>Solicitar la eliminación de tu cuenta y datos personales.</li>
              <li>Revocar en cualquier momento los permisos de acceso a Google.</li>
            </ul>
            <p>Para ejercer cualquiera de estos derechos, contactate con el equipo de Creativedog Agency a través de <a href="mailto:hello@creativedog.agency" style={{ color: "var(--accent)" }}>hello@creativedog.agency</a>.</p>
          </Section>

          <Section title="7. Cambios en esta política">
            <p>
              Creativedog Agency puede actualizar esta política en cualquier momento. Los cambios relevantes serán comunicados a los usuarios activos. El uso continuado de la plataforma implica la aceptación de la política vigente.
            </p>
          </Section>

          <Section title="8. Contacto">
            <p>
              Para consultas sobre privacidad o el manejo de tus datos, escribinos a{" "}
              <a href="mailto:hello@creativedog.agency" style={{ color: "var(--accent)" }}>hello@creativedog.agency</a>.
            </p>
          </Section>

        </div>

        <div style={{ marginTop: 48, paddingTop: 24, borderTop: "1px solid var(--border)", display: "flex", gap: 20, fontSize: 13, color: "var(--text-muted)" }}>
          <Link to="/login" style={{ color: "var(--accent)", textDecoration: "none" }}>← Volver al inicio</Link>
          <Link to="/condiciones-del-servicio" style={{ color: "var(--text-muted)", textDecoration: "none" }}>Condiciones del Servicio →</Link>
        </div>

      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 style={{
        fontSize: 16, fontWeight: 600, color: "var(--text-primary)",
        marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid var(--border)",
      }}>
        {title}
      </h2>
      <div style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.8, display: "flex", flexDirection: "column", gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

function ScopeBlock({ scope, icon, data, use, shared }) {
  return (
    <div style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-md)",
      padding: "16px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <strong style={{ fontSize: 14, color: "var(--text-primary)" }}>{scope}</strong>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "var(--text-secondary)" }}>
        <div>
          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Datos accedidos: </span>
          {data}
        </div>
        <div>
          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Cómo se usan: </span>
          {use}
        </div>
        <div>
          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Compartidos con: </span>
          {shared}
        </div>
      </div>
    </div>
  );
}
